import type { TFieldHarvest, TSortBy, TSortOrder } from "@services/fieldsService";

/** Anything with OpenLayers' `get` — a field Feature, or a stand-in in tests. */
type TSortable = { get(key: string): unknown };

const nameOf = (f: TSortable) => String(f.get("original_name") ?? f.get("name") ?? "");
const areaOf = (f: TSortable) => Number(f.get("area_m2") ?? 0);
const progressOf = (f: TSortable) =>
  (f.get("harvest") as TFieldHarvest | null | undefined)?.harvestPercentage ?? 0;
// The season's crop as the row shows it: crop renames cascade to the field's
// stored name, so this is the same text the list prints.
const cropOf = (f: TSortable) => String(f.get("crop_name") ?? "");

/**
 * The field list's order for a sort chip, worked out in the browser so a chip
 * never refetches every boundary. Same rules as the backend's listFields sort:
 * name, size and crop ascending unless `desc`, harvest progress descending
 * unless `asc`. By crop, fields with no crop come last and each crop's fields
 * are in name order. Names compare without the avg-yield overview's " (12.3)"
 * suffix. Other ties keep the incoming order. Returns a new array.
 */
export const sortFieldFeatures = <F extends TSortable>(
  features: F[],
  sortBy: TSortBy,
  sortOrder: TSortOrder
): F[] => {
  const sorted = [...features];
  if (sortBy === "harvestProgress") {
    const dir = sortOrder === "asc" ? 1 : -1;
    return sorted.sort((a, b) => (progressOf(a) - progressOf(b)) * dir);
  }
  const dir = sortOrder === "desc" ? -1 : 1;
  if (sortBy === "size") return sorted.sort((a, b) => (areaOf(a) - areaOf(b)) * dir);
  if (sortBy === "crop") {
    return sorted.sort((a, b) => {
      const ca = cropOf(a);
      const cb = cropOf(b);
      if (!ca !== !cb) return ca ? -1 : 1;
      return ca.localeCompare(cb) * dir || nameOf(a).localeCompare(nameOf(b));
    });
  }
  return sorted.sort((a, b) => nameOf(a).localeCompare(nameOf(b)) * dir);
};
